/**
 * pushApi.ts — talks to our own /api/push-register and /api/push-unregister
 * (see apps/mobile/api/push-*.ts). Unlike every other network call in this
 * app, this one is NOT proxying a third party — it's our own minimal
 * "account name + push token + display label" registry, the smallest piece
 * of server-side state this self-custody wallet has (see docs/LEARNINGS.md,
 * 2026-09-07, for why: PayCash's own push notifications turned out to
 * require exactly this, just on their infrastructure instead of ours).
 *
 * Never sends a private key, balance, or message content — only what's
 * needed to route a push: the account name, the device's FCM token, the
 * account's local display label (so the notification never has to show the
 * real on-chain name), and the UI language.
 */

import { proxyUrl } from '@properwallet/chain-eos'

export interface PushRegistration {
  account: string
  token: string
  label: string
  lang: string
}

async function post(path: string, body: unknown): Promise<void> {
  try {
    await fetch(proxyUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    // Best effort — a failed registration just means this device won't get
    // pushes until the next sync; never block the UI on it.
  }
}

export function registerPushToken(reg: PushRegistration): Promise<void> {
  return post('/api/push-register', reg)
}

export function unregisterPushToken(account: string, token: string): Promise<void> {
  return post('/api/push-unregister', { account, token })
}
